/**
 * publishState — the ONE projection from a creation's `visibility` to the
 * creator-facing PUBLISH vocabulary. Shared by the studio card chip, the hub
 * header, and the Overview publish control so the three surfaces can never
 * disagree on what a state is called.
 *
 * Vocabulary rules (publish-gate plan, 2026-08-23):
 *  - `public`   → "Published"    — listed in the front-page gallery.
 *  - `unlisted` → "Unpublished"  — NOT listed, but anyone with the link can
 *    play (RLS reads non-draft rows publicly), so copy must never claim
 *    "hidden" or "private".
 *  - `draft`    → "Private fork" — the platform-managed pre-divergence fork
 *    state. Members CANNOT flip it (the builder's divergence is the only
 *    exit), and the word "draft" is reserved for the studio list's
 *    build-status draft (unbuilt wizard resume, "Finish setup") — the chip
 *    never says it for visibility.
 */
import type { Visibility } from '@djgg/creations';

/** The three creator-facing publish states. */
export type PublishState = 'published' | 'unpublished' | 'private-fork';

/** Project `visibility` onto the publish vocabulary. */
export function publishState(visibility: Visibility): PublishState {
  if (visibility === 'public') return 'published';
  if (visibility === 'draft') return 'private-fork';
  return 'unpublished';
}

/** Chip/label copy per state (see the module header for the vocabulary rules). */
export const PUBLISH_STATE_LABEL: Record<PublishState, string> = {
  published: 'Published',
  unpublished: 'Unpublished',
  'private-fork': 'Private fork',
};
